import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, CheckCircle2, Sparkles, Trophy, PenLine, GraduationCap, BookOpen, Users, Star, ShieldCheck, Palette } from "lucide-react";

// Map specialist_id → icon
const ICONS = {
  "grades-tutoring": BookOpen,
  "english-fasttrack": Star,
  "test-prep": GraduationCap,
  "subjects-advisor": ShieldCheck,
  "activities-mentor": Users,
  "portfolio-interview": Palette,
  "sports-scholarships": Trophy,
  "personal-statement": PenLine,
};

export default function Specialists() {
  const [roster, setRoster] = useState([]);
  const [loading, setLoading] = useState(true);
  const [requestingId, setRequestingId] = useState(null);
  const [confirmedIds, setConfirmedIds] = useState(new Set());

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/specialists");
        setRoster(data || []);
      } catch { setRoster([]); }
      finally { setLoading(false); }
    })();
  }, []);

  const request = async (spec) => {
    setRequestingId(spec.id);
    try {
      await api.post("/specialists/request", { specialist_id: spec.id });
      setConfirmedIds((s) => new Set([...s, spec.id]));
      toast.success(`Request logged — we'll email you 3 matched ${spec.title} slots within 24 hours.`);
    } catch (e) {
      if (e?.response?.status === 401) {
        toast("Sign in to book a specialist — your request will be preserved.");
      } else {
        toast.error("Could not log request. Please try again.");
      }
    } finally {
      setRequestingId(null);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-10">
      <div className="label-uppercase">WUA+ · Specialists</div>
      <h1 className="font-editorial text-4xl sm:text-5xl mt-2">Get an expert in your corner.</h1>
      <p className="mt-3 text-stone-600 max-w-2xl">
        For every red gap in your Score Breakdown, there's a specialist who's closed that
        exact gap for hundreds of students. Sports scholarships. IELTS in 6 weeks.
        Cambridge interview coaching. Portfolio reviews. One request — three matched slots
        in your inbox within 24 hours.
      </p>

      <div className="mt-3 inline-flex items-center gap-2 text-xs font-mono text-stone-500 border border-stone-200 rounded-full px-3 py-1">
        <Sparkles className="h-3 w-3 text-[#4C1D95]" />
        Delivered via our Pearsana partner network · No card required to request
      </div>

      {/* First-load flash intentionally suppressed — the roster fetch is
          fast and the card grid below has skeleton-friendly spacing. */}

      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
        {roster.map((s) => {
          const Icon = ICONS[s.id] || Sparkles;
          const confirmed = confirmedIds.has(s.id);
          return (
            <Card
              key={s.id}
              data-testid={`specialist-card-${s.id}`}
              className="p-5 border-stone-200 hover:border-[#4C1D95] transition-colors">
              <div className="flex items-start gap-3">
                <div className="rounded-md bg-[#4C1D95]/10 text-[#4C1D95] p-2.5 shrink-0">
                  <Icon className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-editorial text-xl leading-snug">{s.title}</div>
                  <div className="text-xs text-stone-500 font-mono mt-0.5">
                    {s.advisor_name}
                  </div>
                  <div className="text-sm text-stone-700 mt-3 leading-relaxed">
                    <span className="font-semibold">Uplift:</span> {s.avg_grade_lift}
                  </div>
                  <div className="text-xs text-stone-600 mt-2">
                    <span className="font-mono">${s.typical_fee_usd.toLocaleString()}</span>
                    <span className="text-stone-400 mx-1">·</span>
                    <span className="italic">{s.fee_unit}</span>
                    <span className="text-stone-400 mx-1">·</span>
                    <span>{s.session_format}</span>
                  </div>
                  {s.target_gaps && s.target_gaps.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {s.target_gaps.map((g) => (
                        <span key={g}
                              className="text-[10px] font-underground-sc tracking-wider bg-stone-100 text-stone-700 rounded-full px-2 py-0.5">
                          {g}
                        </span>
                      ))}
                    </div>
                  )}

                  {confirmed ? (
                    <div className="mt-4 flex items-center gap-2 text-sm text-emerald-800">
                      <CheckCircle2 className="h-4 w-4 text-emerald-700" />
                      <span>Request logged — check your email in ≤24h.</span>
                    </div>
                  ) : (
                    <Button
                      data-testid={`specialist-request-${s.id}`}
                      onClick={() => request(s)}
                      disabled={requestingId === s.id}
                      className="mt-4 bg-[#4C1D95] hover:bg-[#3B0764] text-white"
                      size="sm">
                      {requestingId === s.id
                        ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Requesting…</>
                        : <>Request specialist match</>}
                    </Button>
                  )}
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
